import { Component, computed, effect, inject, Injector, signal, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { CrmFormService, CrmInjectionToken, ObjectType } from '@galaxy/crm/static';
import { CRMCustomObjectTypeApiService, CustomObjectTypeInterface, FieldType } from '@vendasta/crm';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { KeyValuePair } from '@vendasta/ai-assistants';
import { of } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { CustomCapabilityConfigurationForm } from '../../../../../core/forms';
import { CRM_UPDATE_MORE_ENTITIES_ENABLED_TOKEN, PLATFORM_LOCATION_TOKEN } from '../../../../../core/tokens';
import { PlatformLocation } from '../../../../../core/ai-assistant.constants';

export interface CrmUpdateFieldConfig {
  fieldId: string;
  fieldName: string;
  fieldDescription: string;
  fieldType: FieldType;
  namespace: string;
  overwrite: boolean;
}

export type FieldSchema = Omit<CrmUpdateFieldConfig, 'overwrite'>;

export interface FieldGroup {
  readonly fields: WritableSignal<CrmUpdateFieldConfig[]>;
  readonly addControl: FormControl<FieldSchema | null>;
  readonly searchControl: FormControl<string>;
  readonly filteredSchemas: Signal<FieldSchema[]>;
  add(schema: FieldSchema | null): void;
  remove(field: CrmUpdateFieldConfig): void;
  setOverwrite(field: CrmUpdateFieldConfig, overwrite: boolean): void;
}

export interface CustomObjectFieldGroup {
  customObjectTypeId: string;
  singularName: string;
  pluralName: string;
  fieldConfigs: CrmUpdateFieldConfig[];
}

@Component({
  selector: 'ai-crm-update-capability-form',
  imports: [
    ReactiveFormsModule,
    MatExpansionModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    NgxMatSelectSearchModule,
    TranslateModule,
    GalaxyFormFieldModule,
  ],
  providers: [CrmFormService],
  templateUrl: './crm-update-capability-form.component.html',
  styleUrl: './crm-update-capability-form.component.scss',
})
export class CrmUpdateCapabilityFormComponent extends CustomCapabilityConfigurationForm {
  private readonly injector = inject(Injector);
  private readonly crmFormService = inject(CrmFormService, { optional: true });
  private readonly crmDependencies = inject(CrmInjectionToken, { optional: true });
  private readonly customObjectTypeApi = inject(CRMCustomObjectTypeApiService, { optional: true });
  private readonly platformLocation = inject(PLATFORM_LOCATION_TOKEN, { optional: true });

  protected readonly moreEntitiesEnabled = toSignal(
    inject(CRM_UPDATE_MORE_ENTITIES_ENABLED_TOKEN, { optional: true }) ?? of(false),
    { initialValue: false },
  );

  protected readonly contactGroup: FieldGroup;
  protected readonly companyGroup: FieldGroup;
  protected readonly opportunityGroup: FieldGroup;
  protected readonly showOpportunitySection = computed(
    () => this.moreEntitiesEnabled() && this.platformLocation === PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP,
  );

  private readonly baseFieldSections: {
    group: FieldGroup;
    labelKey: string;
    addFieldKey: string;
    objectTypeKey: string;
  }[];

  protected readonly fieldSections = computed(() => [
    ...this.baseFieldSections,
    ...(this.showOpportunitySection()
      ? [
          {
            group: this.opportunityGroup,
            labelKey: 'AI_ASSISTANT.SETTINGS.CRM_UPDATE.OPPORTUNITY_FIELDS_LABEL',
            addFieldKey: 'AI_ASSISTANT.SETTINGS.CRM_UPDATE.ADD_OPPORTUNITY_FIELD',
            objectTypeKey: 'AI_ASSISTANT.SETTINGS.CRM_UPDATE.OPPORTUNITY',
          },
        ]
      : []),
  ]);

  private readonly customObjectTypes = toSignal(
    this.crmDependencies && this.customObjectTypeApi
      ? this.crmDependencies.namespace$.pipe(
          switchMap((ns) => this.customObjectTypeApi!.listCustomObjectTypes({ namespace: ns })),
          map((resp) => resp?.customObjectTypes ?? []),
          catchError(() => of([])),
          shareReplay({ bufferSize: 1, refCount: true }),
        )
      : of([]),
    { initialValue: [] as CustomObjectTypeInterface[] },
  );

  private readonly customObjectFieldGroupCache = new Map<string, FieldGroup>();

  protected readonly customObjectSections = signal<{ type: CustomObjectTypeInterface; group: FieldGroup }[]>([]);

  private rawCustomObjectFieldsConfig: CustomObjectFieldGroup[] = [];

  constructor() {
    super();
    this.contactGroup = this.createFieldGroup('Contact');
    this.companyGroup = this.createFieldGroup('Company');
    this.opportunityGroup = this.createFieldGroup('Opportunity');

    this.baseFieldSections = [
      {
        group: this.contactGroup,
        labelKey: 'AI_ASSISTANT.SETTINGS.CRM_UPDATE.CONTACT_FIELDS_LABEL',
        addFieldKey: 'AI_ASSISTANT.SETTINGS.CRM_UPDATE.ADD_CONTACT_FIELD',
        objectTypeKey: 'AI_ASSISTANT.SETTINGS.CRM_UPDATE.CONTACT',
      },
      {
        group: this.companyGroup,
        labelKey: 'AI_ASSISTANT.SETTINGS.CRM_UPDATE.COMPANY_FIELDS_LABEL',
        addFieldKey: 'AI_ASSISTANT.SETTINGS.CRM_UPDATE.ADD_COMPANY_FIELD',
        objectTypeKey: 'AI_ASSISTANT.SETTINGS.CRM_UPDATE.COMPANY',
      },
    ];

    toObservable(this.customObjectTypes)
      .pipe(takeUntilDestroyed())
      .subscribe((types) => {
        for (const type of types) {
          const typeId = type.customObjectTypeId!;
          if (!this.customObjectFieldGroupCache.has(typeId)) {
            this.customObjectFieldGroupCache.set(typeId, this.createFieldGroup('CustomObject', typeId));
          }
        }

        const sections = types.map((type) => ({
          type,
          group: this.customObjectFieldGroupCache.get(type.customObjectTypeId!)!,
        }));

        // Populate groups before setting sections so saveConfiguration() below sees correct state
        for (const section of sections) {
          const saved = this.rawCustomObjectFieldsConfig.find(
            (c) => c.customObjectTypeId === section.type.customObjectTypeId,
          );
          if (saved) {
            section.group.fields.set(saved.fieldConfigs.map((f) => this.normalizeFieldConfig(f)));
          }
        }

        this.customObjectSections.set(sections);

        const currentTypeIds = new Set(types.map((t) => t.customObjectTypeId));
        const hasOrphanedConfigs = this.rawCustomObjectFieldsConfig.some(
          (c) => !currentTypeIds.has(c.customObjectTypeId),
        );
        if (hasOrphanedConfigs) {
          this.saveConfiguration();
        }
      });

    effect(() => {
      const configForm = this.configurationForm();
      this.initializeFromConfig(configForm.value);
    });
  }

  private createFieldGroup(objectType: ObjectType, objectSubtype?: string): FieldGroup {
    const fields = signal<CrmUpdateFieldConfig[]>([]);
    const addControl = new FormControl<FieldSchema | null>(null);
    const searchControl = new FormControl('', { nonNullable: true });

    const fieldSchemas = toSignal(
      this.crmFormService
        ? this.crmFormService.listAllObjectFields$(objectType, objectSubtype).pipe(
            map((schemas) =>
              schemas
                .filter((s) => s.fieldId && s.fieldName)
                .map((s) => ({
                  fieldId: s.fieldId!,
                  fieldName: s.fieldName!,
                  fieldDescription: s.fieldDescription || '',
                  fieldType: s.fieldType ?? FieldType.FIELD_TYPE_STRING,
                  namespace: s.namespace ?? '',
                })),
            ),
            catchError((err) => {
              console.error(`Failed to load ${objectType} field schemas:`, err);
              return of([]);
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
          )
        : of([]),
      { initialValue: [] as FieldSchema[], injector: this.injector },
    );

    const searchTerm = toSignal(searchControl.valueChanges.pipe(startWith('')), {
      initialValue: '',
      injector: this.injector,
    });
    const availableSchemas = computed(() => {
      const selectedIds = new Set(fields().map((f) => f.fieldId));
      return fieldSchemas().filter((s) => !selectedIds.has(s.fieldId));
    });
    const filteredSchemas = computed(() => {
      const term = (searchTerm() ?? '').toLowerCase();
      const schemas = availableSchemas();
      if (!term) return schemas;
      return schemas.filter(
        (s) =>
          s.fieldName.toLowerCase().includes(term) ||
          s.fieldId.toLowerCase().includes(term) ||
          s.fieldDescription.toLowerCase().includes(term),
      );
    });

    const saveConfiguration = (): void => this.saveConfiguration();

    return {
      fields,
      addControl,
      searchControl,
      filteredSchemas,
      add(schema) {
        if (!schema) return;
        const current = fields();
        if (current.some((f) => f.fieldId === schema.fieldId)) return;
        fields.set([
          ...current,
          {
            ...schema,
            fieldType: schema.fieldType ?? FieldType.FIELD_TYPE_STRING,
            namespace: schema.namespace ?? '',
            overwrite: false,
          },
        ]);
        addControl.setValue(null);
        searchControl.setValue('');
        saveConfiguration();
      },
      remove(field) {
        fields.set(fields().filter((f) => f.fieldId !== field.fieldId));
        saveConfiguration();
      },
      setOverwrite(field, overwrite) {
        fields.set(fields().map((f) => (f.fieldId === field.fieldId ? { ...f, overwrite } : f)));
        saveConfiguration();
      },
    };
  }

  private initializeFromConfig(rawConfig: unknown): void {
    const configMap = new Map<string, string>();
    if (rawConfig && Array.isArray(rawConfig)) {
      for (const kv of rawConfig as { key: string; value: string }[]) {
        configMap.set(kv.key, kv.value);
      }
    }

    const contactFieldsJson = configMap.get('contactFields');
    const companyFieldsJson = configMap.get('companyFields');

    let contactFields: CrmUpdateFieldConfig[] = [];
    let companyFields: CrmUpdateFieldConfig[] = [];

    if (contactFieldsJson) {
      try {
        const parsed = JSON.parse(contactFieldsJson);
        if (Array.isArray(parsed) && parsed.every((obj) => this.isCrmUpdateFieldConfig(obj))) {
          contactFields = parsed.map((f) => this.normalizeFieldConfig(f));
        }
      } catch (error) {
        console.warn('Failed to parse contactFields config:', error);
      }
    }

    if (companyFieldsJson) {
      try {
        const parsed = JSON.parse(companyFieldsJson);
        if (Array.isArray(parsed) && parsed.every((obj) => this.isCrmUpdateFieldConfig(obj))) {
          companyFields = parsed.map((f) => this.normalizeFieldConfig(f));
        }
      } catch (error) {
        console.warn('Failed to parse companyFields config:', error);
      }
    }

    this.contactGroup.fields.set(contactFields);
    this.companyGroup.fields.set(companyFields);

    if (this.showOpportunitySection()) {
      const opportunityFieldsJson = configMap.get('opportunityFields');
      let opportunityFields: CrmUpdateFieldConfig[] = [];
      if (opportunityFieldsJson) {
        try {
          const parsed = JSON.parse(opportunityFieldsJson);
          if (Array.isArray(parsed) && parsed.every((obj) => this.isCrmUpdateFieldConfig(obj))) {
            opportunityFields = parsed.map((f) => this.normalizeFieldConfig(f));
          }
        } catch (error) {
          console.warn('Failed to parse opportunityFields config:', error);
        }
      }
      this.opportunityGroup.fields.set(opportunityFields);
    }

    const customObjectFieldsJson = configMap.get('customObjectFields');
    this.rawCustomObjectFieldsConfig = [];
    if (customObjectFieldsJson) {
      try {
        const parsed = JSON.parse(customObjectFieldsJson);
        if (Array.isArray(parsed)) {
          this.rawCustomObjectFieldsConfig = parsed.filter((obj) => this.isCustomObjectFieldGroup(obj));
        }
      } catch (error) {
        console.warn('Failed to parse customObjectFields config:', error);
      }
    }
  }

  private isCrmUpdateFieldConfig(obj: unknown): obj is CrmUpdateFieldConfig {
    const o = obj as CrmUpdateFieldConfig;
    return (
      typeof obj === 'object' &&
      obj !== null &&
      typeof o.fieldId === 'string' &&
      typeof o.fieldName === 'string' &&
      typeof o.overwrite === 'boolean'
    );
  }

  private isCustomObjectFieldGroup(obj: unknown): obj is CustomObjectFieldGroup {
    const o = obj as CustomObjectFieldGroup;
    return (
      typeof obj === 'object' &&
      obj !== null &&
      typeof o.customObjectTypeId === 'string' &&
      Array.isArray(o.fieldConfigs)
    );
  }

  private normalizeFieldConfig(
    raw: CrmUpdateFieldConfig & { fieldType?: FieldType | string | number; namespace?: string },
  ): CrmUpdateFieldConfig {
    const fieldTypeMap = FieldType as unknown as Record<string, FieldType>;
    const parsed =
      typeof raw.fieldType === 'string'
        ? (fieldTypeMap[raw.fieldType] ?? FieldType.FIELD_TYPE_STRING)
        : ((raw.fieldType ?? FieldType.FIELD_TYPE_STRING) as FieldType);
    return {
      ...raw,
      fieldType: parsed,
      namespace: raw.namespace ?? '',
    };
  }

  private fieldConfigToSerializable(field: CrmUpdateFieldConfig): Record<string, unknown> {
    const fieldTypeStr =
      typeof field.fieldType === 'string'
        ? field.fieldType
        : ((FieldType as Record<number, string>)[field.fieldType] ?? 'FIELD_TYPE_STRING');
    return {
      ...field,
      fieldType: fieldTypeStr,
    };
  }

  private saveConfiguration(): void {
    const configForm = this.configurationForm();
    const contactFields = this.contactGroup.fields();
    const companyFields = this.companyGroup.fields();

    const config: KeyValuePair[] = [];
    if (contactFields.length > 0) {
      config.push(
        new KeyValuePair({
          key: 'contactFields',
          value: JSON.stringify(contactFields.map((f) => this.fieldConfigToSerializable(f))),
        }),
      );
    }
    if (companyFields.length > 0) {
      config.push(
        new KeyValuePair({
          key: 'companyFields',
          value: JSON.stringify(companyFields.map((f) => this.fieldConfigToSerializable(f))),
        }),
      );
    }

    if (this.showOpportunitySection()) {
      const opportunityFields = this.opportunityGroup.fields();
      if (opportunityFields.length > 0) {
        config.push(
          new KeyValuePair({
            key: 'opportunityFields',
            value: JSON.stringify(opportunityFields.map((f) => this.fieldConfigToSerializable(f))),
          }),
        );
      }
    }

    const customObjectFields = this.customObjectSections()
      .filter((s) => s.group.fields().length > 0)
      .map((s) => ({
        customObjectTypeId: s.type.customObjectTypeId!,
        singularName: s.type.singularObjectName ?? '',
        pluralName: s.type.pluralObjectName ?? '',
        fieldConfigs: s.group.fields().map((f) => this.fieldConfigToSerializable(f)),
      }));

    if (customObjectFields.length > 0) {
      config.push(
        new KeyValuePair({
          key: 'customObjectFields',
          value: JSON.stringify(customObjectFields),
        }),
      );
    }

    configForm.setValue(config.length > 0 ? config : []);
    configForm.markAsDirty();
  }
}
